// src/pages/dashboard/PollutantDisplay.js
import {
  Box,
  Heading,
  VStack,
  HStack,
  Text,
  Tooltip,
  Spacer, // Import Spacer if needed for specific layout adjustments
} from "@chakra-ui/react";
import { useEnvData } from '../../../context/EnvDataContext'; // Adjust path as needed

// Define thresholds and colors for each pollutant
const POLLUTANT_CONFIG = {
  pm25: {
    displayName: "PM2.5",
    unit: "µg/m³",
    thresholds: [
      { limit: 25, color: "green.400", label: "ดี" }, // Good
      { limit: 37, color: "yellow.400", label: "ปานกลาง" }, // Moderate
      { limit: 50, color: "orange.400", label: "เริ่มมีผลกระทบฯ" }, // Unhealthy for Sensitive
      { limit: 90, color: "red.400", label: "มีผลกระทบฯ" }, // Unhealthy
      { limit: Infinity, color: "purple.400", label: "อันตรายมาก" } // Very Unhealthy
    ],
    gaugeMax: 120
  },
  pm10: {
    displayName: "PM10",
    unit: "µg/m³",
    thresholds: [
      { limit: 50, color: "green.400", label: "ดี" },
      { limit: 80, color: "yellow.400", label: "ปานกลาง" },
      { limit: 120, color: "orange.400", label: "เริ่มมีผลกระทบฯ" },
      { limit: 180, color: "red.400", label: "มีผลกระทบฯ" },
      { limit: Infinity, color: "purple.400", label: "อันตรายมาก" }
    ],
    gaugeMax: 200
  },
  tsp: {
    displayName: "TSP",
    unit: "µg/m³",
    thresholds: [
      { limit: 100, color: "green.400", label: "ดี" },
      { limit: 180, color: "yellow.400", label: "ปานกลาง" },
      { limit: 330, color: "orange.400", label: "เริ่มมีผลกระทบฯ" },
      { limit: 420, color: "red.400", label: "มีผลกระทบฯ" },
      { limit: Infinity, color: "purple.400", label: "อันตรายมาก" }
    ],
    gaugeMax: 450
  }
};

const VolumeGauge = ({ value, config }) => {
  const { thresholds, gaugeMax, unit } = config;
  const numericValue = parseFloat(value);
  const validValue = value !== null && !isNaN(numericValue);

  const numSegments = thresholds.length;
  if (numSegments === 0) return null;
  const segmentHeightPercent = 100 / numSegments;

  return (
    <VStack spacing={0} align="stretch" h="100%" justifyContent="space-between">
        <Text fontSize="9px" color="gray.500" textAlign="center" lineHeight="1">
          {gaugeMax}
        </Text>
        <Box 
            position="relative" 
            h={{base: "150px", md:"180px", lg:"200px"}} // Responsive height for gauge bar
            w={{base: "25px", md:"30px"}} // Responsive width
            bg="gray.50" 
            borderRadius="sm" 
            overflow="hidden" 
            borderWidth="1px" 
            borderColor="gray.300"
        >
        {thresholds.map((thresholdItem, segmentIndex) => {
            const lowerBoundOfColorRegion = segmentIndex === 0 ? 0 : thresholds[segmentIndex - 1].limit;
            const isSegmentLit = validValue && numericValue > lowerBoundOfColorRegion;
            const segmentColor = isSegmentLit ? thresholdItem.color : "gray.200";
            
            // Visual segments are ordered from bottom (index 0) to top
            const bottomPercent = segmentIndex * segmentHeightPercent;

            return (
            <Tooltip
                key={segmentIndex}
                label={`${thresholdItem.label}: ${lowerBoundOfColorRegion}${thresholdItem.limit === Infinity ? '+' : ' - ' + thresholdItem.limit} ${unit}`}
                placement="right"
                hasArrow
            >
                <Box
                position="absolute"
                bottom={`${bottomPercent}%`}
                h={`${segmentHeightPercent}%`}
                w="100%"
                bg={segmentColor}
                borderBottom={segmentIndex < numSegments - 1 ? "1px solid" : "none"} // เส้นคั่นระหว่างช่อง
                borderColor="whiteAlpha.600" // สีเส้นคั่น
                transition="background-color 0.3s ease-in-out"
                />
            </Tooltip>
            );
        })}

        {/* Current Value Indicator Line */}
        {validValue && numericValue >= 0 && ( // แสดงเฉพาะเมื่อมีค่าที่ถูกต้อง
            <Tooltip label={`ค่าปัจจุบัน: ${numericValue.toFixed(1)} ${unit}`} placement="left" hasArrow>
            <Box
                position="absolute"
                bottom={`${Math.min(100, Math.max(0, (numericValue / gaugeMax) * 100))}%`}
                left="-3px" // ปรับให้ยื่นออกมาเล็กน้อย
                w="calc(100% + 6px)" // กว้างกว่าเกจนิดหน่อย
                h="2.5px"
                bg="rgba(0,0,0,0.7)" // สีดำโปร่งแสง
                zIndex={10}
                transition="bottom 0.3s ease-in-out"
            >
                {/* Pointer (optional) */}
                <Box 
                    position="absolute" 
                    right="-1px" 
                    top="50%" 
                    transform="translateY(-50%) translateX(100%)" 
                    width="0" height="0" 
                    borderTop="4px solid transparent"
                    borderBottom="4px solid transparent"
                    borderLeft={`6px solid rgba(0,0,0,0.7)`}
                />
            </Box>
            </Tooltip>
        )}
        </Box>
        <Text fontSize="9px" color="gray.500" textAlign="center" lineHeight="1">
          0
        </Text>
    </VStack>
  );
};


export const PollutantDisplay = ({ pollutant, deviceId }) => {
  const { devicesData } = useEnvData();
  const deviceData = devicesData?.[deviceId];
  const pollutantConfig = POLLUTANT_CONFIG[pollutant] || POLLUTANT_CONFIG.pm25; // Default to PM2.5 config if unknown

  const rawValue = deviceData?.sensorData?.[pollutant];
  const value = (rawValue !== null && rawValue !== undefined && !isNaN(parseFloat(rawValue)))
                ? parseFloat(rawValue)
                : null;

  const getPollutantColor = (val) => {
    if (val === null) return "gray.600";
    for (const threshold of pollutantConfig.thresholds) {
      if (val <= threshold.limit) return threshold.color;
    }
    return pollutantConfig.thresholds[pollutantConfig.thresholds.length - 1]?.color || "gray.600";
  };

  const displayValue = value !== null ? value.toFixed(1) : "-";

  return (
    <Box w="100%" p={{ base: 1, md: 2 }}> {/* ลด padding นอกสุด */}
      <HStack 
        ml={10}
        spacing={{ base: 2, md: 3 }} 
        align="center" // จัดกึ่งกลางแนวตั้งสำหรับ HStack ทั้งหมด
        justifyContent="space-between" // กระจาย space ระหว่าง text และ gauge
        h="100%" // ให้ HStack สูงเต็มพื้นที่ที่จัดสรร
      >
        {/* Text Section */}
        <VStack 
          align="flex-start" 
          spacing={0} 
          flexShrink={1} // ป้องกันการดัน layout
          overflow="hidden" // ป้องกัน content ล้น
          justifyContent="center" // จัดกึ่งกลางเนื้อหาใน VStack
          minW="60%" // กำหนดความกว้างขั้นต่ำให้ส่วนข้อความ
        >
          <Heading 
            size={{ base: "lg", lg: "xl" }} 
            color="gray.500" 
            noOfLines={1}
            fontWeight="semibold"
            mb={5}
          >
            {pollutantConfig.displayName}
          </Heading>
          <HStack align="baseline" spacing={1} mt={'-2px !important'}> {/* Use important if needed to override theme spacing */}
            <Heading
              fontSize={{ base: "xxl", sm: "60px", md: "80px", lg:"120px" }} // Responsive font sizes
              color={getPollutantColor(value)}
              lineHeight={{base: "1", md:"0.9"}} // ปรับ lineHeight
              letterSpacing={{base: "-0.04em", md:"-0.05em"}} // ลดระยะห่างตัวอักษร
              fontWeight="bold"
            >
              {displayValue}
            </Heading>
            {value !== null && ( // แสดงหน่วยเฉพาะเมื่อมีค่า
              <Text 
                fontSize={{ base: "sm", md: "md", lg: "lg" }} 
                color="gray.500" 
                fontWeight="medium" 
                pb={{base:1, md:2}} //ดันหน่วยลงเล็กน้อย
                alignSelf="flex-end" // จัดให้หน่วยอยู่ท้าย baseline
              >
                {pollutantConfig.unit}
              </Text>
            )}
          </HStack>
        </VStack>

        {/* Gauge Section */}
        <Box 
          display="flex" 
          alignItems="center" // จัดเกจให้อยู่กึ่งกลางแนวตั้งของ Box นี้
          justifyContent="center" // จัดเกจให้อยู่กึ่งกลางแนวนอนของ Box นี้
          h="full" // ให้ Box สูงเต็ม
          pl={{ base: 1, md: 2 }} // Padding ซ้ายเล็กน้อย
        >
          <VolumeGauge value={value} config={pollutantConfig} />
        </Box>
      </HStack>
    </Box>
  );
};